import type { ReactNode } from 'react';
import { t } from '../../i18n';
import { CheckIcon, CrossIcon, PersonIcon } from '../../shared/icons';
import { Card, CardHeading, Gap, Media, Meta, Muted, Notice, StatusBadge } from '../../shared/ui';

/**
 * Решение модерации — одна карточка на оба экрана.
 *
 * Решение это одна сущность, и вид у неё один: лента игрока и журнал
 * персонала показывают одно и то же. Разница ровно одна — персоналу приезжает
 * ещё и ПЕРСОНАЖ, чью вещь разбирали; у игрока персонаж свой и называть его
 * незачем. Поэтому он необязательный и встаёт в ту же строку фактов.
 *
 * Пока карточек было две, они разошлись: у персонала комментарий шёл голым
 * абзацем, модератор — строкой фактов, а персонаж — отдельной строкой под
 * названием.
 */
export function DecisionCard({
  approved,
  title,
  target,
  character = null,
  date,
  comment,
  moderator,
  details,
}: {
  approved: boolean;
  title: string;
  /** Вид вещи. Пустой, когда он УЖЕ стал заголовком: у вещи без названия. */
  target: string | null;
  /** Персонажа называют только персоналу — у игрока он один и он свой. */
  character?: string | null;
  date: string;
  comment: string | null;
  moderator: string;
  /**
   * «Что было в этой версии». Только на ленте игрока: ручка версии проверяет
   * ВЛАДЕНИЕ, а не роль, и на чужую версию отвечает 404 — персоналу этот блок
   * показал бы ошибку.
   */
  details?: ReactNode;
}) {
  return (
    <Card tone={approved ? 'published' : 'rejected'}>
      {/* Название тянется, вердикт прижат к правому краю — так состояния
          выстраиваются колонкой и список читается по ней. */}
      <CardHeading badge={<VerdictBadge approved={approved} />}>{title}</CardHeading>

      <Gap step="s1h" />
      <Meta>{[target, character, date]}</Meta>

      {comment === null ? null : (
        <>
          <Gap step="s2h" />
          <Notice tone={approved ? 'info' : 'error'}>{comment}</Notice>
        </>
      )}

      <Gap step="s1h" />
      {/* Кто решил — подписью внизу со значком: имя без пояснения читалось бы
          как автор заявки, а слово «Модератор:» занимало треть строки. */}
      <Media aside={<PersonIcon tone="muted" />}>
        <Muted>{moderator}</Muted>
      </Media>

      {details}
    </Card>
  );
}

/**
 * Вердикт ОДНИМ знаком: значок внутри той же плашки, что и слово.
 *
 * Отдельный квадратик с галочкой рядом со словом — это одно и то же, сказанное
 * дважды.
 */
function VerdictBadge({ approved }: { approved: boolean }) {
  return (
    <StatusBadge tone={approved ? 'published' : 'rejected'}>
      {approved ? <CheckIcon size={11} /> : <CrossIcon size={11} />}
      {approved ? t('common.verdict.approved') : t('common.verdict.rejected')}
    </StatusBadge>
  );
}
